import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { openModalAction } from '../../../../actions/modal';
import Strings from '../../../../Strings';
import { makeValid } from '../../../../utils';
import { getProviderDisplayName } from '../../../../utils/externalProvider';
import { hasPermission, PERMISSIONS } from '../../../../utils/userPermissions';
import {
  EDIT_EXTERNAL_PROVIDER_MODAL_NAME,
  EXTERNAL_PROVIDER_TYPES,
} from '../../../../modals/EditExternalProviderModal';
import { actions } from '../redux/actions';
import TableWidget from '../component/TableWidget';

function ExternalProviderDetails(props) {
  const {
    provider,
    providerType,
    patientId,
    enrollmentId,
    onRefresh,
    editable = true,
    openEditModal,
    openConfirmModal,
  } = props;
  const isPrimaryCareProvider = providerType === EXTERNAL_PROVIDER_TYPES.primaryCare;
  const providerLabel = isPrimaryCareProvider ? Strings.primaryCareProvider : Strings.referringProvider;
  const canEdit = editable && hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO_EDIT);
  const specialty = [provider?.specialtyName, provider?.specialtyCode].filter(Boolean).join(' — ');

  const data = provider
    ? [
        [Strings.externalProviderDirectory.providerName, makeValid(getProviderDisplayName(provider))],
        [Strings.externalProviderDirectory.npi, makeValid(provider.npi)],
        [Strings.status, makeValid(provider.status)],
        [Strings.externalProviderDirectory.specialty, makeValid(specialty)],
        [Strings.organization, makeValid(provider.organizationName)],
        [Strings.externalProviderDirectory.directAddress, makeValid(provider.directAddress)],
        [Strings.externalProviderDirectory.faxNumber, makeValid(provider.faxNumber)],
      ]
    : [];

  const openEditor = () => {
    openEditModal({
      providerType,
      provider,
      patientId,
      enrollmentId,
      onSuccess: onRefresh,
    });
  };

  const removeProvider = () => {
    openConfirmModal({
      caption: Strings.externalProviderDirectory.removeProvider,
      title: Strings.formatString(Strings.externalProviderDirectory.removeProviderConfirmation, providerLabel),
      hideCaution: true,
      onConfirmAction: isPrimaryCareProvider
        ? actions.updatePrimaryCareProvider(patientId, null)
        : actions.updateReferringProvider(enrollmentId, null),
      onSuccessNotification: Strings.formatString(Strings.externalProviderDirectory.providerRemoved, providerLabel),
      confirmPostAction: onRefresh,
    });
  };

  let buttons;
  if (canEdit && provider) {
    buttons = [
      {
        icon: 'edit',
        onClick: openEditor,
        id: `change-${providerType}-button`,
        label: Strings.formatString(Strings.externalProviderDirectory.changeProviderTitle, providerLabel),
      },
      {
        icon: 'delete',
        onClick: removeProvider,
        id: `remove-${providerType}-button`,
        label: Strings.formatString(Strings.externalProviderDirectory.removeProviderTitle, providerLabel),
      },
    ];
  } else if (canEdit) {
    buttons = [
      {
        icon: 'plus',
        onClick: openEditor,
        id: `add-${providerType}-button`,
        label: Strings.formatString(Strings.externalProviderDirectory.addProviderTitle, providerLabel),
      },
    ];
  }

  return <TableWidget title={providerLabel} buttons={buttons} data={data} firstColumnClassName="center-details-col" />;
}

ExternalProviderDetails.propTypes = {
  provider: PropTypes.object,
  providerType: PropTypes.oneOf(Object.values(EXTERNAL_PROVIDER_TYPES)).isRequired,
  patientId: PropTypes.string.isRequired,
  enrollmentId: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  openEditModal: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

ExternalProviderDetails.widgetId = 'PrimaryCareProviderWidget';

const mapDispatchToProps = dispatch => ({
  openEditModal: data => dispatch(openModalAction(EDIT_EXTERNAL_PROVIDER_MODAL_NAME, data)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(null, mapDispatchToProps)(ExternalProviderDetails);
